import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AuthService from '../services/authService';

const MIN_QUOTA_LOADING_MS = 420;
const delayMs = ms => (ms > 0 ? new Promise(r => setTimeout(r, ms)) : Promise.resolve());

const INITIAL_QUOTA = { used: 0, limit: 0, bonus: 0, remaining: 0, loading: true };

function calcPct(quota) {
    if (quota.unlimited || quota.remaining === -1 || Number(quota.limit) <= 0) return 0;
    const total = (quota.limit || 0) + (quota.bonus || 0);
    if (total <= 0) return 0;
    return Math.min(100, Math.round((quota.used / total) * 100));
}

export function useQuotaStatus(queryType, clientPath, noLimit = false) {
    const location = useLocation();
    const apiPath = !noLimit ? `/naver/quota/status/${queryType}` : null;
    const eventName = `${queryType}QuotaUpdated`;

    const [quota, setQuota] = useState(INITIAL_QUOTA);

    const fetchQuota = useCallback(async () => {
        if (!apiPath) return;
        const start = Date.now();
        try {
            const { data } = await AuthService.authAxios.get(apiPath, {
                params: { ensureRow: 1 },
            });
            await delayMs(Math.max(0, MIN_QUOTA_LOADING_MS - (Date.now() - start)));
            if (data?.ok) {
                const { used, limit, bonus, remaining, unlimited } = data;
                const lim = Number(limit);
                const rem = Number(remaining);
                const unlimitedNorm =
                    !!unlimited || rem === -1 || !Number.isFinite(lim) || lim <= 0;
                setQuota({
                    used,
                    limit,
                    bonus,
                    remaining: unlimitedNorm ? -1 : remaining,
                    unlimited: unlimitedNorm,
                    loading: false,
                });
            } else {
                setQuota(q => ({ ...q, loading: false }));
            }
        } catch {
            await delayMs(Math.max(0, MIN_QUOTA_LOADING_MS - (Date.now() - start)));
            setQuota(q => ({ ...q, loading: false }));
        }
    }, [apiPath]);

    useEffect(() => {
        if (!clientPath) return;
        if (location.pathname === clientPath) {
            setQuota(q => ({ ...q, loading: true }));
            fetchQuota();
        }
    }, [location.pathname, clientPath, fetchQuota]);

    useEffect(() => {
        if (location.pathname !== clientPath || !eventName) return;
        const onUpdate = () => fetchQuota();
        window.addEventListener(eventName, onUpdate);
        return () => window.removeEventListener(eventName, onUpdate);
    }, [location.pathname, clientPath, eventName, fetchQuota]);

    const pct = useMemo(() => calcPct(quota), [quota]);
    return { quota, pct, loading: quota.loading };
}
